import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import {
  Button,
  Card,
  Popconfirm,
  message,
  Space,
  Breadcrumb,
  Tag,
  Descriptions,
  Spin,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Checkbox,
  Select,
  Upload,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  SendOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

const BroadcastNotificationDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [form] = Form.useForm();
  const [users, setUsers] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await ajaxService.get(`/broad-cast-notifications/${id}`);
      if (response.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError("Error fetching data");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await ajaxService.get("/broadcast-notifications/users");
      if (response.success) {
        setUsers(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      const response = await ajaxService.delete("/broad-cast-notifications/", id);
      if (response.success) {
        message.success("Broadcast deleted successfully");
        history.push("/broad-cast-notifications");
      } else {
        message.error("Failed to delete broadcast");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error deleting broadcast");
    }
  }, [id, history]);

  const handleSend = async () => {
    try {
      const response = await ajaxService.post(`/broadcast-notifications/${id}/send`);
      if (response.success) {
        message.success("Broadcast sent successfully");
        fetchData(id);
      } else {
        message.error("Failed to send broadcast");
      }
    } catch (err) {
      console.error("Send error:", err);
      message.error("Error sending broadcast");
    }
  };

  const openEditModal = async () => {
    await fetchUsers();
    const rec = typeof data.recipients === 'string' ? JSON.parse(data.recipients) : data.recipients || [];
    const isAll = rec.includes('all');
    setIsAllChecked(isAll);
    setSelectedUsers(isAll ? [] : rec.filter(uid => typeof uid === 'string' && !isNaN(uid)));
    form.setFieldsValue({
      title: data.title,
      message: data.message,
      image: data.image_url ? [{ uid: -1, name: data.image_url, status: 'done', url: data.image_url }] : [],
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (values) => {
    const formData = new FormData();
    formData.append('title', values.title);
    formData.append('message', values.message);
    if (values.image && values.image[0]?.originFileObj) {
      formData.append('image', values.image[0].originFileObj);
    }

    let recipients = [];
    if (isAllChecked) {
      recipients = ['all'];
    } else if (selectedUsers.length > 0) {
      recipients = selectedUsers;
    }
    formData.append('recipients', JSON.stringify(recipients));

    try {
      const response = await ajaxService.put("/broad-cast-notifications/", id, formData);
      if (response.success) {
        message.success("Broadcast updated successfully");
        fetchData(id);
        setShowEditModal(false);
      } else {
        message.error("Failed to update broadcast");
      }
    } catch (err) {
      console.error("Update error:", err);
      message.error("Error updating broadcast");
    }
  };

  const handleAllChange = (e) => {
    setIsAllChecked(e.target.checked);
    if (e.target.checked) {
      setSelectedUsers([]);
    }
  };

  const handleUserSelectChange = (value) => {
    setSelectedUsers(value);
  };

  useEffect(() => {
    if (id) fetchData(id);
  }, [id, fetchData]);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: 'var(--spacing-2xl)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--error-red)' }}>{error}</p>
    </div>
  );
  if (!data) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-gray)' }}>No data found</p>
    </div>
  );

  const getStatusText = (stateId) => {
    switch (stateId) {
      case 1: return 'Draft';
      case 2: return 'Scheduled';
      case 3: return 'Sent';
      default: return 'Unknown';
    }
  };

  const getRecipientsText = (recipients) => {
    if (!recipients) return 'N/A';
    const rec = typeof recipients === 'string' ? JSON.parse(recipients) : recipients;
    if (rec.includes('all')) return 'All Users';
    return `${rec.length} Users`;
  };

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Header Section */}
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to="/dashboard" style={{ color: 'var(--text-white)' }}>
                    <HomeOutlined /> Home
                  </Link>
                ),
              },
              { title: <Link to="/broadcast" style={{ color: 'var(--text-white)' }}>Broadcast Notifications</Link> },
              {
                title: <span style={{ color: 'var(--text-gray)' }}>{data?.title ? `${data.title.substring(0, 20)}...` : "Details"}</span>,
              },
            ]}
          />
          <Space>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={openEditModal}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Edit
            </Button>
            {data.state_id !== 3 && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                style={{
                  background: 'var(--success-green)',
                  borderColor: 'var(--success-green)',
                }}
              >
                Send
              </Button>
            )}
            <Popconfirm
              title="Are you sure to delete this broadcast?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                danger 
                icon={<DeleteOutlined />}
                style={{
                  background: 'var(--error-red)',
                  borderColor: 'var(--error-red)',
                }}
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/broadcast")}
              style={{
                background: 'var(--dark-bg-card)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)',
              }}
            >
              Back
            </Button>
          </Space>
        </div>
        <h1 className="module-title">Broadcast Notification Details</h1>
        <p className="module-subtitle">View and manage broadcast notification information</p>
      </div>

      <Card 
        style={{
          width: '100%',
          boxShadow: 'var(--shadow-card)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--dark-bg-card)',
          border: '1px solid rgba(254, 2, 125, 0.2)',
        }}
      >
        {/* Details */}
        <Descriptions
          bordered
          column={1}
          labelStyle={{ fontWeight: "bold", width: "200px", color: 'var(--text-white)' }}
        >
          <Descriptions.Item label="ID"><span style={{ color: 'var(--text-white)' }}>{data.id}</span></Descriptions.Item>
          <Descriptions.Item label="Title"><span style={{ color: 'var(--text-white)' }}>{data.title || "N/A"}</span></Descriptions.Item>
          <Descriptions.Item label="Message"><span style={{ color: 'var(--text-white)' }}>{data.message || "N/A"}</span></Descriptions.Item>
          <Descriptions.Item label="Image URL">
            {data.image_url ? (
              <a 
                href={data.image_url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'var(--primary-purple)' }}
              >
                View Image
              </a>
            ) : (
              <span style={{ color: 'var(--text-white)' }}>N/A</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Recipients"><span style={{ color: 'var(--text-white)' }}>{getRecipientsText(data.recipients)}</span></Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusText(data.state_id) === 'Sent' ? "var(--success-green)" : "var(--primary-purple)"}>
              {getStatusText(data.state_id)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Sent At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.sent_at
                ? moment(data.sent_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.created_at
                ? moment(data.created_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.updated_at
                ? moment(data.updated_at).format("YYYY-MM-DD HH:mm:ss")
                : "N/A"}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Edit Modal */}
      <Modal
        title={<span style={{ color: 'var(--text-white)' }}>Edit Broadcast Notification</span>}
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        width={600}
        className="modal-overlay"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)' }}>Title</span>}
                name="title"
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input 
                  placeholder="Enter title" 
                  className="input-field"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)' }}>Message</span>}
                name="message"
                rules={[{ required: true, message: "Message is required" }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Enter message" 
                  className="input-field"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={<span style={{ color: 'var(--text-white)' }}>Image</span>}
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) return e;
                  return e?.fileList || [];
                }}
              >
                <Upload
                  name="image"
                  listType="picture"
                  maxCount={1}
                  beforeUpload={() => false}
                  accept="image/*"
                >
                  <Button 
                    icon={<UploadOutlined />}
                    style={{
                      background: 'var(--primary-purple)',
                      borderColor: 'var(--primary-purple)',
                      color: 'white',
                    }}
                  >
                    Select Image
                  </Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={<span style={{ color: 'var(--text-white)' }}>Recipients</span>}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox 
                    onChange={handleAllChange} 
                    checked={isAllChecked}
                    style={{ color: 'var(--text-white)' }}
                  >
                    Send to All Users
                  </Checkbox>
                  {!isAllChecked && (
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Select users"
                      value={selectedUsers}
                      onChange={handleUserSelectChange}
                      className="input-field"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {users.map(user => (
                        <Option key={user.id} value={user.id.toString()}>{user.name || user.email}</Option>
                      ))}
                    </Select>
                  )}
                </Space>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Button 
              type="primary" 
              htmlType="submit"
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
              }}
            >
              Update
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BroadcastNotificationDetails;